import { useState, type RefObject } from 'react'
import { Skeleton } from '../../platform/async/Skeleton'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { Checkbox } from '../../platform/primitives/Checkbox'
import '../../platform/primitives/dialog.css'
import './reports.css'

/** The server's own cap per field; one more is a 422. */
export const MAX_PER_FILTER = 50

export type FilterChoice = { ids: string[]; none: boolean }

type Props = {
  open: boolean
  /** "Accounts", "Owners", "Category groups" or "Tags": the dialog is named `Filter by {field}`. */
  field: string
  options: { id: string; label: string }[]
  /** The field's "none" option ("Uncategorised", "Untagged"), or null when it has none. */
  noneLabel: string | null
  selected: FilterChoice
  /** A list still loading is a skeleton, never an empty list. */
  loading?: boolean
  /** The list failed to load: say so, with a way back, rather than claim there is nothing to filter by. */
  failed?: { retry: () => void } | null
  onApply: (choice: FilterChoice) => void
  onClose: () => void
  restoreFocusTo?: RefObject<HTMLElement | null>
}

/**
 * One filter field's checkbox list. Values within a field are ORed, so checking two accounts means
 * "either"; the none option sits first, beside the values, as part of the same field. Apply commits;
 * Cancel and Escape discard.
 */
export function FilterDialog({ open, field, options, noneLabel, selected, loading = false, failed = null, onApply, onClose, restoreFocusTo }: Props) {
  return (
    <Overlay open={open} onClose={onClose} label={`Filter by ${field}`} restoreFocusTo={restoreFocusTo}>
      {open && (
        <FilterForm
          field={field}
          options={options}
          noneLabel={noneLabel}
          selected={selected}
          loading={loading}
          failed={failed}
          onApply={onApply}
          onClose={onClose}
        />
      )}
    </Overlay>
  )
}

function FilterForm({ field, options, noneLabel, selected, loading, failed, onApply, onClose }: Omit<Props, 'open' | 'restoreFocusTo'>) {
  const [ids, setIds] = useState(() => new Set(selected.ids))
  const [none, setNone] = useState(selected.none)
  const full = ids.size >= MAX_PER_FILTER
  const toggle = (id: string, on: boolean) =>
    setIds((prev) => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  return (
    <form
      className="dialog"
      onSubmit={(e) => {
        e.preventDefault()
        onApply({ ids: [...ids], none })
      }}
    >
      <h2 className="dialog__title">{`Filter by ${field}`}</h2>
      {failed ? (
        <div>
          <p className="note" role="status">{`${field} couldn't be loaded.`}</p>
          <Button type="button" variant="chip" onClick={failed.retry}>
            Try again
          </Button>
        </div>
      ) : loading ? (
        <div aria-hidden="true">
          <Skeleton height={14} />
          <Skeleton height={14} />
          <Skeleton height={14} />
        </div>
      ) : options.length === 0 && noneLabel === null ? (
        <p className="note">Nothing to filter by yet.</p>
      ) : (
        <fieldset className="rp-filter__list">
          <legend className="rp-filter__legend">{field}</legend>
          {noneLabel !== null && (
            <div className="rp-filter__none">
              <label className="rp-filter__option">
                <Checkbox checked={none} onChange={setNone} label={noneLabel} />
                <span>{noneLabel}</span>
              </label>
            </div>
          )}
          {options.map((o) => {
            const on = ids.has(o.id)
            return (
              <label key={o.id} className="rp-filter__option">
                <Checkbox
                  checked={on}
                  onChange={(next) => toggle(o.id, next)}
                  label={o.label}
                  disabled={!on && full}
                  describedBy={!on && full ? 'rp-filter-cap' : undefined}
                />
                <span>{o.label}</span>
              </label>
            )
          })}
        </fieldset>
      )}
      {full && (
        <p id="rp-filter-cap" className="note">
          Up to {MAX_PER_FILTER} per filter.
        </p>
      )}
      <div className="dialog__actions">
        <Button type="submit" variant="primary" disabled={Boolean(failed) || loading}>
          Apply
        </Button>
        <Button type="button" variant="chip" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
